/**
 * The left column of the Ankiety tab: the mandatory form, then the trainer's own, then the three
 * states that have no cards at all.
 *
 * ── `pending`, `error`, `empty` and `noResults` are FOUR states, never a branch on the data ───
 * `rows.length === 0` cannot tell a read still running from one that answered with nothing, and it
 * cannot tell either from a filter that matched none. `useSurveysTab` switches on the reads' own
 * status and on whether the list is narrowed; each state below is reached from exactly one answer.
 *
 * ── The wait is drawn as the cards it is about to be ─────────────────────────────────────────
 * Not a sentence and not a spinner. The skeleton uses the CARD's own class, so its reserve is the
 * card's own geometry — and the gate hides the bars, never the box, so a fast answer draws no
 * skeleton and moves nothing.
 *
 * ── The card is a BOX with a button in it, not a button ──────────────────────────────────────
 * KANON `01-ankiety.html` puts a switch on every check-in card, and a control inside a control is
 * not allowed. The shape is the review queue's, which solved the same problem: the `.card` is the
 * drawn object, `.cardButton` is the target that fills it and carries `aria-current`, and the
 * switch is a SIBLING laid over the head row's right end — where the canon draws it — in the gap
 * `.headSlot` reserves for it. Nothing overlaps: the slot is as wide as the switch.
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '~/shared/ui/EmptyState';
import { NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { ChevronRightIcon, LockIcon } from '~/shared/ui/AuthIcons';
import ghost from '~/shared/ui/ghostButton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import { Toggle } from '~/shared/ui/Toggle';

import type { ClientFace } from '../model/clientFaces';
import {
  assignedCountLabel,
  cadenceLabel,
  questionCountLabel,
  statusLabel,
  type SurveyGroups,
  type SurveyRow,
} from '../model/surveyList';
import { noResultsReason, type SurveyFacets } from '../model/surveyFilters';
import type { SurveyListState } from '../lib/useSurveysTab';
import { AvatarStack } from '~/features/survey-question-editor';
import { ClockIcon, StarIcon } from '~/shared/ui/ReportIcons';
import { PublicFormIcon } from '~/shared/ui/PublicFormIcons';
import canon from '~/shared/ui/surveyCanon.module.css';
import styles from './SurveyColumn.module.css';

/** Four, the canon's own card count — so the wait fills the column the way the list will. */
const SKELETON_CARDS = 4;

interface SurveyCardProps {
  readonly row: SurveyRow;
  readonly selected: boolean;
  readonly faces: ReadonlyMap<number, ClientFace>;
  readonly onSelect: (id: number) => void;
  /** True while this row's pause is in flight. The switch is refused until the server answers. */
  readonly pausing: boolean;
  readonly onSetActive: (id: number, active: boolean) => void;
}

function SurveyCard({ row, selected, faces, onSelect, pausing, onSetActive }: SurveyCardProps) {
  const { t } = useTranslation();
  const cadence = cadenceLabel(row, t);
  const status = statusLabel(row, t);
  // The public form's template is neither paused nor assigned from here (public-form R4): it draws
  // no switch and no roster, like the intake, and says who fills it in instead.
  const leadForm = row.leadForm === true;
  const fixed = row.mandatory || leadForm;

  return (
    <li className={[
      String(styles.card),
      row.mandatory ? String(styles.cardIntake) : '',
      selected ? String(styles.cardOn) : '',
      row.mandatory || row.active ? '' : String(styles.cardPaused),
    ]
      .filter(Boolean)
      .join(' ')}
    >
      <button
        type="button"
        // `aria-current` rather than `aria-pressed`: the card is not a toggle, it is the row the
        // panel beside it is describing. The brighter edge is the second channel, never the only one.
        aria-current={selected ? 'true' : undefined}
        className={styles.cardButton}
        onClick={() => {
          onSelect(row.id);
        }}
      >
        <span className={styles.head}>
          {/* The one accent on this column — the mark that says a form is not optional. */}
          {row.mandatory ? <StarIcon className={styles.mandatoryMark} /> : null}
          <span className={styles.title}>{row.title}</span>
          {row.mandatory ? (
            <span className={canon.chip}>
              <LockIcon className={canon.chipIcon} />
              {t('web:reports.surveys.mandatoryChip')}
            </span>
          ) : leadForm ? (
            <span className={canon.chip}>{t('web:publicForm.builder.reports.chip')}</span>
          ) : cadence === null ? null : (
            <span className={canon.chip}>
              <ClockIcon className={canon.chipIcon} />
              {cadence}
            </span>
          )}
          {/* The switch's own room, reserved inside the button so the title and the chip stop where
            * the canon stops them and the switch never lands on either. */}
          {row.mandatory ? null : <span className={styles.headSlot} aria-hidden />}
        </span>

        <span className={styles.meta}>
          {questionCountLabel(row, t)}
          {row.mandatory ? (
            <> · {t('web:reports.surveys.intakeWhen')}</>
          ) : leadForm ? (
            <> · {t('web:publicForm.builder.reports.when')}</>
          ) : status === null ? null : (
            <>
              {' · '}
              <span className={row.active ? styles.statusActive : undefined}>{status}</span>
            </>
          )}
          <ChevronRightIcon className={styles.chevron} />
        </span>

        {/* The mandatory form is assigned to everybody by construction, so it has no roster row —
          * which is also why its card takes the shorter reserve. */}
        {fixed ? null : (
          <span className={styles.roster}>
            <span className={styles.rosterCount}>{assignedCountLabel(row, t)}</span>
            <span className={styles.rosterFaces}>
              <AvatarStack
                customerIds={row.assignedCustomerIds}
                count={row.assignedCount}
                faces={faces}
              />
            </span>
          </span>
        )}
      </button>

      {/* Outside the button, over the slot it reserved. `PATCH … { isActive }` on press — this is
        * the one write the list makes, and it is not optimistic; see `useSurveysTab`. */}
      {/* The public form's template is styled and shared from its builder, so its card carries the
        * way there where a check-in carries its switch (owner, 28 September 2026). */}
      {leadForm ? (
        <span className={styles.toggleSlot}>
          <Link
            to="/strona"
            className={`${String(canon.ibtn)} ${String(canon.ibtnBare)}`}
            aria-label={t('web:publicForm.builder.nav')}
            title={t('web:publicForm.builder.nav')}
          >
            <PublicFormIcon className={canon.ibtnIcon} />
          </Link>
        </span>
      ) : null}
      {fixed ? null : (
        <span className={styles.toggleSlot}>
          <Toggle
            checked={row.active}
            label={t('web:reports.surveys.activeToggle', { title: row.title })}
            lockedReason={pausing ? t('web:reports.surveys.pausing') : null}
            onChange={(active) => {
              onSetActive(row.id, active);
            }}
          />
        </span>
      )}
    </li>
  );
}

function ColumnSkeleton({ skeleton }: { readonly skeleton: boolean }) {
  const { t } = useTranslation();

  return (
    <div className={styles.group} aria-busy="true">
      {/* The word a listener needs; the bars are decorative `<i>`s with no name of their own. */}
      <p className={hidden.srOnly}>{t('web:reports.surveys.states.loading')}</p>
      {Array.from({ length: SKELETON_CARDS }, (_, index) => (
        <div key={index} className={styles.card}>
          <div className={styles.cardButton}>
          {skeleton && (
            <>
              <span className={styles.head}>
                <i className={`${String(skel.bar)} ${String(styles.skelTitle)}`} />
                <i className={`${String(skel.bar)} ${String(styles.skelChip)}`} />
              </span>
              <i className={`${String(skel.bar)} ${String(styles.skelMeta)}`} />
              <span className={styles.roster}>
                <i
                  className={`${String(skel.bar)} ${String(skel.disc)} ${String(styles.skelFace)}`}
                />
              </span>
            </>
          )}
          </div>
        </div>
      ))}
    </div>
  );
}

interface SurveyColumnProps {
  readonly state: SurveyListState;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly groups: SurveyGroups;
  readonly facets: SurveyFacets;
  readonly faces: ReadonlyMap<number, ClientFace>;
  readonly selectedId: number | null;
  readonly onSelect: (id: number) => void;
  readonly pausingId: number | null;
  readonly onSetActive: (id: number, active: boolean) => void;
  readonly onClearFilters: () => void;
  readonly onRetry: () => void;
}

export function SurveyColumn({
  state,
  skeleton,
  reveal,
  groups,
  facets,
  faces,
  selectedId,
  onSelect,
  pausingId,
  onSetActive,
  onClearFilters,
  onRetry,
}: SurveyColumnProps) {
  const { t } = useTranslation();

  if (state === 'pending') {
    return (
      <div className={styles.column}>
        <ColumnSkeleton skeleton={skeleton} />
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className={styles.column}>
        <div className={styles.state}>
          <EmptyState
            mark={<NoteMarkIcon />}
            tone="neutral"
            title={t('web:reports.surveys.states.errorTitle')}
            hint={t('web:reports.surveys.states.errorMessage')}
          />
          <button
            type="button"
            className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
            onClick={onRetry}
          >
            {t('web:reports.surveys.states.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (state === 'empty') {
    return (
      <div className={styles.column}>
        <div className={styles.state}>
          <EmptyState
            mark={<NoteMarkIcon />}
            tone="action"
            title={t('web:reports.surveys.empty.title')}
            hint={t('web:reports.surveys.empty.message')}
          />
          {/* The one empty state on this page that DESCRIBES an action, in the slot the other three
            * already put a button in. A card inviting a trainer to make their first check-in and
            * then pointing at a „+" somewhere above it is an instruction where a control belongs. */}
          <Link
            to="/raporty/nowa"
            className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
          >
            {t('web:reports.surveys.empty.action')}
          </Link>
        </div>
      </div>
    );
  }

  if (state === 'noResults') {
    return (
      <div className={styles.column}>
        <div className={styles.state}>
          <EmptyState
            mark={<NoteMarkIcon />}
            tone="neutral"
            title={t('web:reports.surveys.noResults.title')}
            hint={t(`web:reports.surveys.noResults.${noResultsReason(facets)}`, {
              term: facets.query.trim(),
            })}
          />
          <button
            type="button"
            className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
            onClick={onClearFilters}
          >
            {t('web:reports.surveys.noResults.clear')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${String(styles.column)} ${reveal}`}>
      {groups.intake === null ? null : (
        <section className={styles.group} aria-labelledby="reports-group-mandatory">
          <h2 className={`${String(canon.eyebrow)} ${String(styles.eyebrow)}`} id="reports-group-mandatory">
            {t('web:reports.surveys.groups.mandatory')}
          </h2>
          <ul className={styles.list}>
            <SurveyCard
              row={groups.intake}
              selected={groups.intake.id === selectedId}
              faces={faces}
              onSelect={onSelect}
              pausing={false}
              onSetActive={onSetActive}
            />
          </ul>
        </section>
      )}

      {groups.leadForm == null ? null : (
        <section className={styles.group} aria-labelledby="reports-group-lead-form">
          <h2 className={`${String(canon.eyebrow)} ${String(styles.eyebrow)}`} id="reports-group-lead-form">
            {t('web:publicForm.builder.reports.group')}
          </h2>
          <ul className={styles.list}>
            <SurveyCard
              row={groups.leadForm}
              selected={groups.leadForm.id === selectedId}
              faces={faces}
              onSelect={onSelect}
              pausing={false}
              onSetActive={onSetActive}
            />
          </ul>
        </section>
      )}

      {groups.checkIns.length === 0 ? null : (
        <section className={styles.group} aria-labelledby="reports-group-own">
          <h2 className={`${String(canon.eyebrow)} ${String(styles.eyebrow)}`} id="reports-group-own">
            {t('web:reports.surveys.groups.own')}
          </h2>
          <ul className={styles.list}>
            {groups.checkIns.map((row) => (
              <SurveyCard
                key={row.id}
                row={row}
                selected={row.id === selectedId}
                faces={faces}
                onSelect={onSelect}
                pausing={pausingId === row.id}
                onSetActive={onSetActive}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
